import {
    Button,
    Card,
    Col,
    DatePicker,
    Form,
    InputNumber,
    Row,
    Switch,
    Typography,
    message,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useApi } from '../contexts/ApiContext';
import {
    type ZeroCommissionPromotionFormValues,
    readPromotionFormValues,
    toPromotionConfigurationRows,
    validatePromotionForm,
} from '../domain/zeroCommissionPromotionSettings';

const { Paragraph } = Typography;

const DAY_FORMAT = 'YYYY-MM-DD';

/**
 * The form holds days as `Dayjs` for the pickers, and a cleared number input as `null`; the
 * domain module works in YYYY-MM-DD and plain numbers.
 */
interface PromotionFormFields
    extends Omit<
        ZeroCommissionPromotionFormValues,
        | 'firstContractFreeDays'
        | 'firstContractLoanLimitRon'
        | 'firstContractStartDate'
        | 'sosFromDate'
        | 'sosToDate'
    > {
    firstContractFreeDays: number | null;
    firstContractLoanLimitRon: number | null;
    firstContractStartDate: Dayjs | null;
    sosFromDate: Dayjs | null;
    sosToDate: Dayjs | null;
}

const toDayjs = (day: string | null): Dayjs | null => (day === null ? null : dayjs(day));

const toDay = (value: Dayjs | null | undefined): string | null =>
    value ? value.format(DAY_FORMAT) : null;

const toFields = (values: ZeroCommissionPromotionFormValues): PromotionFormFields => ({
    ...values,
    firstContractStartDate: toDayjs(values.firstContractStartDate),
    sosFromDate: toDayjs(values.sosFromDate),
    sosToDate: toDayjs(values.sosToDate),
});

const toValues = (fields: PromotionFormFields): ZeroCommissionPromotionFormValues => ({
    firstContractEnabled: fields.firstContractEnabled,
    firstContractFreeDays: fields.firstContractFreeDays ?? 0,
    firstContractLoanLimitRon: fields.firstContractLoanLimitRon ?? 0,
    firstContractStartDate: toDay(fields.firstContractStartDate),
    sosEnabled: fields.sosEnabled,
    sosFromDate: toDay(fields.sosFromDate),
    sosToDate: toDay(fields.sosToDate),
});

/**
 * The zero-commission promotions applied at payment (BL-02 first contract, BL-03 SOS campaign).
 * Both are off until an administrator switches them on here; the Payment page then waives the
 * days they cover.
 */
export default function ZeroCommissionPromotionsSettings() {
    const { t } = useTranslation();
    const { configuration } = useApi();
    const [form] = Form.useForm<PromotionFormFields>();
    const [saving, setSaving] = useState(false);
    const firstContractEnabled = Form.useWatch('firstContractEnabled', form) ?? false;
    const sosEnabled = Form.useWatch('sosEnabled', form) ?? false;

    const load = useCallback(async () => {
        try {
            const response = await configuration.getAll();
            form.setFieldsValue(toFields(readPromotionFormValues(response.data ?? {})));
        } catch (error) {
            console.error('Error loading the promotion settings:', error);
            message.error(t('configuration.loadError'));
        }
    }, [configuration, form, t]);

    useEffect(() => {
        void load();
    }, [load]);

    const save = async (fields: PromotionFormFields) => {
        const values = toValues(fields);
        const errors = validatePromotionForm(values);
        if (errors.length > 0) {
            message.error(t(`configuration.promotions.errors.${errors[0]}`));
            return;
        }
        setSaving(true);
        try {
            await configuration.updateBatch(toPromotionConfigurationRows(values));
            message.success(t('configuration.saveSuccess'));
        } catch (error) {
            console.error('Error saving the promotion settings:', error);
            message.error(t('configuration.saveError'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card
            title={t('configuration.promotions.title')}
            style={{ marginTop: 16 }}
            data-testid="zero-commission-promotions"
        >
            <Paragraph type="secondary">{t('configuration.promotions.description')}</Paragraph>
            <Form
                form={form}
                layout="vertical"
                onFinish={fields => void save(fields)}
                initialValues={toFields(readPromotionFormValues({}))}
            >
                <Form.Item
                    name="firstContractEnabled"
                    valuePropName="checked"
                    label={t('configuration.promotions.firstContract')}
                    extra={t('configuration.promotions.firstContractHelp')}
                >
                    <Switch data-testid="promotion-first-contract-switch" />
                </Form.Item>
                <Row gutter={16}>
                    <Col xs={24} md={8}>
                        <Form.Item
                            name="firstContractFreeDays"
                            label={t('configuration.promotions.freeDays')}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                precision={0}
                                disabled={!firstContractEnabled}
                                data-testid="promotion-first-contract-days"
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item
                            name="firstContractLoanLimitRon"
                            label={t('configuration.promotions.loanLimit')}
                        >
                            <InputNumber
                                style={{ width: '100%' }}
                                min={0}
                                precision={0}
                                disabled={!firstContractEnabled}
                                data-testid="promotion-first-contract-limit"
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item
                            name="firstContractStartDate"
                            label={t('configuration.promotions.signedFrom')}
                        >
                            <DatePicker
                                style={{ width: '100%' }}
                                format={DAY_FORMAT}
                                disabled={!firstContractEnabled}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item
                    name="sosEnabled"
                    valuePropName="checked"
                    label={t('configuration.promotions.sos')}
                    extra={t('configuration.promotions.sosHelp')}
                >
                    <Switch data-testid="promotion-sos-switch" />
                </Form.Item>
                <Row gutter={16}>
                    <Col xs={24} md={8}>
                        <Form.Item name="sosFromDate" label={t('configuration.promotions.from')}>
                            <DatePicker
                                style={{ width: '100%' }}
                                format={DAY_FORMAT}
                                disabled={!sosEnabled}
                            />
                        </Form.Item>
                    </Col>
                    <Col xs={24} md={8}>
                        <Form.Item name="sosToDate" label={t('configuration.promotions.to')}>
                            <DatePicker
                                style={{ width: '100%' }}
                                format={DAY_FORMAT}
                                disabled={!sosEnabled}
                            />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item>
                    <Button
                        type="primary"
                        htmlType="submit"
                        loading={saving}
                        data-testid="promotion-save"
                    >
                        {t('configuration.save')}
                    </Button>
                </Form.Item>
            </Form>
        </Card>
    );
}
